<template>
  <div class="flex w-full flex-col items-center justify-center gap-y-6">
    <CandidateShopCartProgressBar
      v-if="currentStep !== 4 && data.product_variants.length > 0"
      size="size-6"
      :total-steps="totalSteps"
      :current-step="currentStep"
    />

    <CandidateShopCartCheckout
      :data="data"
      v-if="currentStep === 1"
      @next="currentStep = currentStep + 1"
      @update-quantity="handleUpdateQuantity"
      @delete-product="handleDeleteProduct"
    >
    </CandidateShopCartCheckout>

    <CandidateShopCartShipping
      :data="data"
      v-model:updateAddress="updateAddress"
      :errors="errors"
      @handle-validate-shipping="handleValidateShipping"
      v-if="currentStep === 2 && data.product_variants.length > 0"
      @next="currentStep = currentStep + 1"
      @cancel="currentStep = currentStep - 1"
    >
    </CandidateShopCartShipping>

    <CandidateShopCartOrderSummary
      v-if="currentStep === 3 && data.product_variants.length > 0"
      @next="currentStep = currentStep + 1"
      @place-order="handlePlaceOrder"
      @cancel="handleCancelShopCartSummary"
      :data="data"
      :has-offline-products="hasOfflineProducts"
      @delete-product="handleDeleteProduct"
      @update-quantity="handleUpdateQuantity"
      :loading-place-order="loadingPlaceOrder"
    >
    </CandidateShopCartOrderSummary>

    <CandidateShopCartOrderPlaced
      v-if="currentStep === 4"
      :show="currentStep === 4"
      @cancel="redirectToPage('redesign.candidate.shop.products.index')"
    ></CandidateShopCartOrderPlaced>
  </div>
</template>

<script setup>
  import { nextTick, onMounted, ref } from 'vue';
  import CandidateShopCartCheckout from './components/CandidateShopCartCheckout.vue';
  import CandidateShopCartOrderSummary from './components/CandidateShopCartOrderSummary.vue';
  import CandidateShopCartProgressBar from './components/CandidateShopCartProgressBar.vue';
  import CandidateShopCartShipping from './components/CandidateShopCartShipping.vue';
  import CandidateShopCartOrderPlaced from './components/CandidateShopCartOrderPlaced.vue';
  import { useToast } from 'vue-toastification';
  import { __, redirectToPage } from '@/helpers';

  const props = defineProps({
    data: {
      type: Object,
      required: true
    },
    errors: {
      type: Object,
      required: false
    }
  });

  const data = ref(props.data);
  const errors = ref(props.errors || {});
  const updateAddress = ref(false);
  const totalSteps = ref(3);
  const currentStep = ref(1);
  const toast = useToast();
  const loadingPlaceOrder = ref(false);
  const hasOfflineProducts = ref(false);
  const checkOfflineProducts = () => {
    hasOfflineProducts.value = data.value.product_variants.some(
      (product_variant) =>
        product_variant.variant.product.delivery_type === 'offline'
    );

    return hasOfflineProducts.value;
  };

  onMounted(async () => {
    await nextTick();

    const storedMessage = sessionStorage.getItem('toast_message');
    if (storedMessage) {
      const { type, message } = JSON.parse(storedMessage);
      toast[type](message);

      sessionStorage.removeItem('toast_message');
    }
    await nextTick();

    const storedCurrentStep = sessionStorage.getItem('currentStep');
    if (storedCurrentStep) {
      currentStep.value = parseInt(storedCurrentStep, 10);
      sessionStorage.removeItem('currentStep');
    } else {
      currentStep.value = 1;
    }

    await nextTick();
  });

  const handleValidateShipping = async () => {
    try {
      data.value.shipping_details.has_no_shipping_products =
        !checkOfflineProducts();
      const response = await axios.post(
        route('redesign.candidate.shop.cart.validate-shipping'),

        data.value.shipping_details
      );

      if (response.data.message) {
        currentStep.value += 1;
      }
    } catch (error) {
      errors.value = error.response?.data?.errors || {};
      toast.error(error.response?.data?.message || __('Something went wrong'));
      // Handle error appropriately, e.g., show a toast notification
    }
  };

  const handleDeleteProduct = async (product_variant_id) => {
    const response = await axios.delete(
      route('redesign.candidate.shop.cart.remove', product_variant_id)
    );

    try {
      if (response.data.message) {
        // i want to set the message to the session so that it can be displayed on the next page
        sessionStorage.setItem(
          'toast_message',
          JSON.stringify({
            type: 'success',
            message: response.data.message
          })
        );
        // how do i refresh
        window.location.reload();
      } else {
        toast.error(__('Failed to remove product from cart'));
      }
    } catch (error) {
      console.error('Error removing product from cart:', error);
      toast.error(__('An error occurred while removing the product from cart'));
    }
  };

  const handlePlaceOrder = async () => {
    loadingPlaceOrder.value = true;
    try {
      data.value = { ...data.value, update_address: updateAddress.value };

      const response = await axios.post(
        route('redesign.candidate.shop.cart.place-order'),
        data.value
      );

      if (response.data.message) {
        sessionStorage.setItem(
          'toast_message',
          JSON.stringify({
            type: 'success',
            message: response.data.message
          })
        );

        sessionStorage.setItem('currentStep', 4);
        // Move to the order placed step
        window.location.reload();
      } else {
        toast.error(__('Failed to place order'));
      }
    } catch (error) {
      console.error('Error placing order:', error);
      toast.error(
        error.response?.data?.message ||
          __('An error occurred while placing the order')
      );
    } finally {
      loadingPlaceOrder.value = false;
    }
  };

  const handleUpdateQuantity = async (eventData) => {
    const productVariant = data.value.product_variants.find(
      (variant) => variant.variant.id === eventData.product_variant_id
    );

    if (
      productVariant.quantity_remaining &&
      productVariant.quantity_remaining < eventData.quantity &&
      eventData.action === 'increase'
    ) {
      if (productVariant.variant.product.max_quantity_per_user)
        toast.error(
          __(
            `This product has a limit of [ ${productVariant.variant.product.max_quantity_per_user} ] per user. You have already purchased [ ${productVariant.quantity_already_bought_by_user} ], so you cannot buy more.`
          )
        );
      return;
    }
    try {
      const response = await axios.post(
        route(
          'redesign.candidate.shop.cart.update-quantity',
          eventData.product_variant_id
        ),
        {
          action: eventData.action,
          quantity: eventData.quantity,
          product_variant_updated_at:
            productVariant.variant.formatted_product_variant_updated_at,
          product_updated_at:
            productVariant.variant.formatted_product_updated_at
        }
      );

      if (response.data.message) {
        toast.success(response.data.message);
        // Update the cart data with the new quantity
        const productVariant = data.value.product_variants.find(
          (variant) => variant.variant.id === eventData.product_variant_id
        );
        if (productVariant) {
          if (eventData.action === 'increase') {
            productVariant.quantity += 1;
            productVariant.quantity_remaining -= 1;
          } else if (
            eventData.action === 'decrease' &&
            productVariant.quantity > 1
          ) {
            productVariant.quantity -= 1;
            productVariant.quantity_remaining += 1;
          }
        }
      } else {
        toast.error(__('Failed to update product quantity'));
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          __('An error occurred while updating the product quantity')
      );
    }
  };

  const handleCancelOnlineProducts = () => {
    window.history.back();
  };

  const handleCancelShopCartSummary = () => {
    return hasOfflineProducts.value
      ? (currentStep.value = currentStep.value - 1)
      : handleCancelOnlineProducts();
  };
</script>
